/**
 * Vue « Vagues » : diagramme alluvial des décisions humaines sur une donne.
 *
 * Abscisse = étape de décision (départ, écart, plis), ordonnée = percentile de rang
 * sur la donne. Les bandes sont empilées par rang moyen croissant, ce qui garantit
 * la lisibilité : positionner chaque bande à son rang moyen absolu les entasserait
 * au centre, les moyennes de groupes de plusieurs dizaines de joueurs étant toutes
 * très proches de la moyenne générale.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND_URL } from '../../config';
import { getCardShortName } from '../../utils/cards';
import {
  buildFlowLayout,
  topDecisionImpacts,
  visibleColumnIndexes,
  DEFAULT_FLOW_LAYOUT_OPTIONS,
  type DecisionFlowReport,
  type FlowBand,
  type FlowOrdering,
} from './decisionFlowLayout';
import {
  buildColorScale,
  formatBranchPerformance,
  shrunkPerformance,
  FLOW_METRIC_LABELS,
  type FlowMetric,
} from './flowPerformance';

const PLOT_HEIGHT = 460;
const AXIS_WIDTH = 54;
const HEADER_HEIGHT = 44;
const ETA_STRIP_HEIGHT = 46;
const MIN_COLUMN_PITCH = 62;
const MAX_COLUMN_PITCH = 120;

export interface DecisionFlowSelection {
  pathKeys: string[];
  ecartKey: string | null;
  label: string;
  /**
   * Effectif max pour le drill-down joueurs. Doit couvrir la population du flux :
   * sinon un segment visible (population « tous ») renvoie une liste vide dès que
   * le top-X de la page est plus étroit.
   */
  drillTopX: number;
}

function formatPct(value: number): string {
  return `${Math.round(value * 100)} %`;
}

function bandCardsLabel(band: FlowBand): string {
  if (band.node.label) return band.node.label;
  if (band.node.cards.length === 0) return band.node.isResidual ? 'diffus' : '—';
  return band.node.cards.map(card => getCardShortName(card as never)).join('/');
}

/**
 * Texte inscrit dans la bande, contraint par ses 26 pixels de large.
 *
 * Les noms de cartes tiennent à l'horizontale, mais un écart de six cartes ou une
 * signature de profil sont bien trop longs : ils sont écrits verticalement, la bande
 * offrant alors toute sa hauteur. Le libellé complet reste dans l'infobulle.
 */
function bandInlineLabel(band: FlowBand): { text: string; vertical: boolean } | null {
  if (band.node.isResidual || band.node.cards.length === 0) return null;

  if (band.column.kind === 'ecart') {
    const text = band.node.label
      || band.node.cards.map(card => getCardShortName(card as never)).join(' ');
    return band.height >= text.length * 6.2 ? { text, vertical: true } : null;
  }

  if (band.height < 12) return null;
  const [first, ...others] = band.node.cards;
  const text = getCardShortName(first as never)
    + (others.length > 0 ? `+${others.length}` : '');
  return { text, vertical: false };
}

function bandTooltip(
  band: FlowBand,
  playerCount: number,
  metric: FlowMetric,
  totalPlayersOnDeal: number,
): string {
  const node = band.node;
  const lines = [
    `${band.column.label} — ${bandCardsLabel(band)}`,
    `${node.count} joueur(s) sur ${playerCount}`,
    `${FLOW_METRIC_LABELS[metric]} moyen : `
      + formatBranchPerformance(node, metric, totalPlayersOnDeal),
    `Rang moyen ${formatPct(node.meanRankPct)} ± ${formatPct(node.ciHalfWidth)}`,
    `Médiane ${formatPct(node.medianRankPct)} (interquartile ${formatPct(node.p25RankPct)} → ${formatPct(node.p75RankPct)})`,
    `Rangs ${node.bestRank} à ${node.worstRank}`,
    `Score moyen ${node.meanDealScore.toFixed(1)}`,
  ];
  if (node.isResidual) lines.push('Toutes les autres décisions, agrégées.');
  else if (band.isContinuation) lines.push('Prolongation : la branche ne se subdivise plus.');
  else if (!node.significant) lines.push('Écart au parent non distinguable du bruit.');
  return lines.join('\n');
}

function decisionFlowQuery(params: {
  population: 'all' | 'topX';
  topX: number;
  groupEquiv: boolean;
  lowRankLimit: number;
  contractTypes: string;
  ecartGrouping: 'exact' | 'profile';
  includeEcart: boolean;
  minCount: number;
  maxChildren: number;
}): string {
  const query: Record<string, string> = {
    population: params.population,
    topX: String(params.topX),
    groupEquiv: String(params.groupEquiv),
    contractTypes: params.contractTypes,
    ecartGrouping: params.ecartGrouping,
    includeEcart: String(params.includeEcart),
    maxChildren: String(params.maxChildren),
  };
  if (params.groupEquiv) query.lowRankLimit = String(params.lowRankLimit);
  if (params.minCount > 0) query.minCount = String(params.minCount);
  return new URLSearchParams(query).toString();
}

export default function DiffereStatsDecisionFlow({
  sessionPseudo,
  differeId,
  dealIndex,
  dealLabel,
  topX,
  groupEquiv,
  lowRankLimit,
  contractTypes,
  selection,
  onSelect,
}: {
  sessionPseudo: string;
  differeId: number;
  dealIndex: number;
  dealLabel: string;
  topX: number;
  groupEquiv: boolean;
  lowRankLimit: number;
  contractTypes: string;
  selection: DecisionFlowSelection | null;
  onSelect: (selection: DecisionFlowSelection | null) => void;
}) {
  const [population, setPopulation] = useState<'all' | 'topX'>('all');
  const [ecartGrouping, setEcartGrouping] = useState<'exact' | 'profile'>('exact');
  const [includeEcart, setIncludeEcart] = useState(true);
  const [minCount, setMinCount] = useState(0);
  const [maxChildren, setMaxChildren] = useState(4);
  const [ordering, setOrdering] = useState<FlowOrdering>('wave');
  const [metric, setMetric] = useState<FlowMetric>('score');
  const [showCards, setShowCards] = useState(false);
  const [onlyDivergent, setOnlyDivergent] = useState(true);
  const [maxColumnIndex, setMaxColumnIndex] = useState(19);

  const [report, setReport] = useState<DecisionFlowReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(960);

  const query = decisionFlowQuery({
    population, topX, groupEquiv, lowRankLimit, contractTypes,
    ecartGrouping, includeEcart, minCount, maxChildren,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    void fetch(
      `${BACKEND_URL}/api/admin/stats/differe/${differeId}/deal/${dealIndex}/decision-flow`
      + `?${query}&pseudo=${encodeURIComponent(sessionPseudo)}`,
    )
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erreur chargement des vagues de décision');
        if (!cancelled) setReport(data as DecisionFlowReport);
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [differeId, dealIndex, query, sessionPseudo]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width && width > 0) setAvailableWidth(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visible = useMemo(
    () => (report ? visibleColumnIndexes(report.columns, { maxColumnIndex, onlyDivergent }) : []),
    [report, maxColumnIndex, onlyDivergent],
  );

  /**
   * Sans les cartes, le nœud se réduit à un repère : le ruban occupe presque tout
   * l'intervalle et la lecture devient celle d'un courant qui se divise. Les afficher
   * impose au contraire d'élargir le nœud pour loger le texte, d'où l'effet de barres.
   */
  const bandWidth = showCards ? 24 : 5;

  /**
   * En arbre, une branche s'arrête là où sa jouerie cesse de diverger, au lieu d'être
   * tirée en trait plat jusqu'au bord droit. L'écart entre sœurs est aussi plus large,
   * puisque le dédoublement est ce qu'on vient lire.
   */
  const isTree = ordering === 'branch';
  const allowsCrossing = ordering === 'wave' || ordering === 'flux';

  const performanceByNode = useMemo(
    () => (report ? shrunkPerformance(report, metric) : null),
    [report, metric],
  );

  const layout = useMemo(() => {
    if (!report) return null;
    const plotWidth = Math.max(320, availableWidth - AXIS_WIDTH - 24);
    const span = Math.max(1, visible.length - 1);
    const pitch = Math.max(
      MIN_COLUMN_PITCH,
      Math.min(MAX_COLUMN_PITCH, (plotWidth - bandWidth) / span),
    );
    const perf = performanceByNode;
    return buildFlowLayout(report, visible, {
      ...DEFAULT_FLOW_LAYOUT_OPTIONS,
      bandWidth,
      columnPitch: pitch,
      plotHeight: PLOT_HEIGHT,
      ordering,
      bandGap: isTree || ordering === 'wave' ? 9 : DEFAULT_FLOW_LAYOUT_OPTIONS.bandGap,
      fillRatio: isTree || ordering === 'wave' ? 0.84 : 1,
      performanceOf: perf
        ? node => perf.get(node) ?? node.meanRankPct
        : undefined,
    });
  }, [report, visible, availableWidth, ordering, bandWidth, isTree, performanceByNode]);

  const impacts = useMemo(() => (layout ? topDecisionImpacts(layout, 5) : []), [layout]);
  const bandById = useMemo(
    () => new Map((layout?.bands ?? []).map(band => [band.id, band])),
    [layout],
  );
  const colorScale = useMemo(
    () => (report ? buildColorScale(report, metric) : null),
    [report, metric],
  );

  const selectedId = useMemo(() => {
    if (!selection || !layout) return null;
    const match = layout.bands.find(band => (
      band.node.ecartKey === selection.ecartKey
      && band.node.pathKeys.length === selection.pathKeys.length
      && band.node.pathKeys.every((key, i) => key === selection.pathKeys[i])
    ));
    return match?.id ?? null;
  }, [selection, layout]);

  const maxEta = useMemo(() => {
    if (!report) return 0;
    return report.columns.reduce((max, column) => Math.max(max, column.eta2Adj), 0);
  }, [report]);

  function handleBandClick(band: FlowBand) {
    if (band.id === 'root') {
      onSelect(null);
      return;
    }
    onSelect({
      pathKeys: band.node.pathKeys,
      ecartKey: band.node.ecartKey,
      label: `${band.column.label} — ${bandCardsLabel(band)}`,
      // « Tous » côté flux = tous les classés de la donne, pas le top-X de la page.
      drillTopX: population === 'all'
        ? Math.max(topX, report?.totalPlayersOnDeal ?? topX, 1)
        : Math.max(1, topX),
    });
  }

  return (
    <div className="differe-stats-flow" ref={containerRef}>
      <div className="differe-stats-flow-controls">
        <label>
          Population
          <select
            value={population}
            onChange={e => setPopulation(e.target.value as 'all' | 'topX')}
          >
            <option value="all">Tous les joueurs</option>
            <option value="topX">Top-{topX}</option>
          </select>
        </label>
        <label>
          Effectif min.
          <input
            type="number"
            min={0}
            max={200}
            value={minCount}
            onChange={e => setMinCount(Math.max(0, Number(e.target.value)))}
            title="0 = seuil automatique (1 % de la population)"
          />
        </label>
        <label>
          Branches max
          <select
            value={maxChildren}
            onChange={e => setMaxChildren(Number(e.target.value))}
            title="Branches conservées sous chaque nœud ; le reste est agrégé en « diffus »"
          >
            {[2, 3, 4, 5, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          Ordre
          <select
            value={ordering}
            onChange={e => {
              const next = e.target.value as FlowOrdering;
              setOrdering(next);
              // En arbre / vagues les colonnes plates n'apprennent rien ; en flux elles ondulent.
              setOnlyDivergent(next !== 'flux');
            }}
            title="Vagues : arrivée libre selon la performance, croisements entre lignées ; ramification : sans croisement"
          >
            <option value="wave">Vagues</option>
            <option value="branch">Ramification</option>
            <option value="flux">Flux</option>
          </select>
        </label>
        <label>
          Performance
          <select
            value={metric}
            onChange={e => setMetric(e.target.value as FlowMetric)}
            title="Grandeur qui donne sa couleur à chaque vague"
          >
            {(Object.keys(FLOW_METRIC_LABELS) as FlowMetric[]).map(key => (
              <option key={key} value={key}>{FLOW_METRIC_LABELS[key]}</option>
            ))}
          </select>
        </label>
        {report?.ecartAvailable && (
          <label className="differe-stats-flow-check">
            <input
              type="checkbox"
              checked={includeEcart}
              onChange={e => setIncludeEcart(e.target.checked)}
              title="Décoché : le départ est le début de la jouerie, sans conditionner sur l'écart"
            />
            Inclure l&apos;écart
          </label>
        )}
        {report?.ecartAvailable && includeEcart && (
          <label>
            Regroupement écart
            <select
              value={ecartGrouping}
              onChange={e => setEcartGrouping(e.target.value as 'exact' | 'profile')}
            >
              <option value="exact">Cartes exactes</option>
              <option value="profile">Profil</option>
            </select>
          </label>
        )}
        <label>
          Jusqu&apos;au pli
          <input
            type="number"
            min={1}
            max={19}
            value={maxColumnIndex}
            onChange={e => setMaxColumnIndex(Math.max(1, Math.min(19, Number(e.target.value))))}
          />
        </label>
        <label className="differe-stats-flow-check">
          <input
            type="checkbox"
            checked={onlyDivergent}
            onChange={e => setOnlyDivergent(e.target.checked)}
          />
          Plis divergents seulement
        </label>
        <label className="differe-stats-flow-check">
          <input
            type="checkbox"
            checked={showCards}
            onChange={e => setShowCards(e.target.checked)}
          />
          Cartes jouées
        </label>
      </div>

      {loading && <p className="admin-muted">Chargement des vagues de décision…</p>}
      {error && <p className="admin-error-text">{error}</p>}

      {report && !loading && !error && report.playerCount === 0 && (
        <p className="admin-muted">
          Aucun joueur exploitable sur {dealLabel}
          {report.ecartUnavailableReason ? ` — ${report.ecartUnavailableReason}` : ''}
        </p>
      )}

      {report && layout && colorScale && !loading && !error && report.playerCount > 0 && (
        <>
          <p className="admin-muted differe-stats-hint">
            {report.playerCount} joueur(s) sur {report.totalPlayersOnDeal} classés — {report.contractLabel}
            {report.hasEcartColumn
              ? ' — colonne écart active (Sud preneur)'
              : report.ecartAvailable
                ? ' — écart abstrait : départ = début de jouerie'
                : `${report.ecartUnavailableReason ? ` — pas de colonne écart : ${report.ecartUnavailableReason.toLowerCase()}` : ''}`}
            {' '}— seuil d&apos;effectif {report.params.minCount}. Hauteur = effectif.
            {ordering === 'wave'
              ? ' Ordonnée = même performance que la couleur : une sous-vague verte arrive haut,'
                + ' une rouge plonge ; les sœurs partent classées vert→rouge dans leur mère.'
              : isTree
                ? ' Chaque vague se dédouble là où la jouerie diverge et s\'arrête là où elle cesse'
                  + ' de diverger au-dessus du seuil ; la couleur dit ce que vaut le sous-groupe.'
                : ' L\'ordonnée est la position dans la donne au pli considéré, mesurée sur les'
                  + ' points de cartes cumulés par le camp du joueur (en tête vers le haut) ; au'
                  + ' dernier pli elle rejoint le classement de la donne.'}
            {' '}Cliquez un courant pour lister les joueurs.
          </p>

          <div className="differe-stats-flow-canvas">
            <svg
              width={AXIS_WIDTH + layout.width + 8}
              height={HEADER_HEIGHT + PLOT_HEIGHT + ETA_STRIP_HEIGHT}
              role="img"
              aria-label={`Vagues de décision — ${dealLabel}`}
            >
              <defs>
                {/*
                  * Dégradé horizontal mère → fille sur chaque ruban : la couleur change
                  * là où la performance change, au lieu d'apparaître d'un coup à l'arrivée.
                  */}
                {layout.ribbons.map(ribbon => {
                  const band = bandById.get(ribbon.toId);
                  const parent = band?.parentId ? bandById.get(band.parentId) : null;
                  if (!band || !parent) return null;
                  if (isTree && band.isContinuation) return null;
                  return (
                    <linearGradient
                      key={`grad-${ribbon.id}`}
                      id={`differe-flow-grad-${ribbon.id}`}
                      gradientUnits="objectBoundingBox"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor={colorScale.color(parent.node)} />
                      <stop offset="100%" stopColor={colorScale.color(band.node)} />
                    </linearGradient>
                  );
                })}
                <pattern
                  id="differe-flow-hatch"
                  width="6"
                  height="6"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(45)"
                >
                  {/* Hachure discrète : sur un courant sinueux, un motif marqué crée
                      plus de bruit visuel que les rubans colorés qu'il doit distinguer. */}
                  <rect width="6" height="6" fill="rgba(100, 116, 139, 0.30)" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(148, 163, 184, 0.22)" strokeWidth="2" />
                </pattern>
              </defs>

              <g transform={`translate(0, ${HEADER_HEIGHT})`}>
                {[0, 0.25, 0.5, 0.75, 1].map(tick => (
                  <g key={tick}>
                    <line
                      x1={AXIS_WIDTH - 6}
                      y1={tick * PLOT_HEIGHT}
                      x2={AXIS_WIDTH + layout.width}
                      y2={tick * PLOT_HEIGHT}
                      className="differe-stats-flow-gridline"
                    />
                    <text
                      x={AXIS_WIDTH - 10}
                      y={tick * PLOT_HEIGHT + 4}
                      textAnchor="end"
                      className="differe-stats-flow-axis-label"
                    >
                      {isTree
                        ? ''
                        : tick === 0
                          ? (ordering === 'wave' ? 'meilleur' : 'en tête')
                          : tick === 1
                            ? (ordering === 'wave' ? 'pire' : 'dernier')
                            : formatPct(tick)}
                    </text>
                  </g>
                ))}
              </g>

              <g transform={`translate(${AXIS_WIDTH}, 0)`}>
                {layout.columns.map(({ column, x }) => (
                  <text
                    key={column.index}
                    x={x + DEFAULT_FLOW_LAYOUT_OPTIONS.bandWidth / 2}
                    y={HEADER_HEIGHT - 26}
                    textAnchor="middle"
                    className="differe-stats-flow-column-label"
                  >
                    {column.kind === 'trick' ? `P${(column.trickIndex ?? 0) + 1}` : column.label}
                  </text>
                ))}
              </g>

              <g transform={`translate(${AXIS_WIDTH}, ${HEADER_HEIGHT})`}>
                {/*
                  * Les rubans sont le sujet du dessin : chacun porte la branche qu'il
                  * alimente, donc sa couleur, son infobulle et son clic. En ordre
                  * « rang global » ils se croisent, et l'opacité pleine masquerait ce
                  * qui passe derrière.
                  */}
                {layout.ribbons.map(ribbon => {
                  const band = bandById.get(ribbon.toId);
                  if (!band) return null;
                  if (isTree && band.isContinuation) return null;
                  const parent = band.parentId ? bandById.get(band.parentId) : null;
                  const fill = parent
                    ? `url(#differe-flow-grad-${ribbon.id})`
                    : colorScale.color(band.node);
                  return (
                    <path
                      key={ribbon.id}
                      d={ribbon.path}
                      className={[
                        'differe-stats-flow-ribbon',
                        selectedId === band.id ? 'is-selected' : '',
                      ].filter(Boolean).join(' ')}
                      fill={fill}
                      fillOpacity={allowsCrossing ? 0.62 : 0.92}
                      onClick={() => handleBandClick(band)}
                    >
                      <title>{bandTooltip(band, report.playerCount, metric, report.totalPlayersOnDeal)}</title>
                    </path>
                  );
                })}

                {/*
                  * Les agrégats « autres » gardent la couleur de leur performance — la
                  * teindre en gris les sortirait de l'échelle et ferait lire un niveau
                  * qu'ils n'ont pas. Seule une hachure par-dessus signale l'agrégat.
                  */}
                {layout.ribbons.map(ribbon => {
                  const band = bandById.get(ribbon.toId);
                  if (!band || !band.node.isResidual) return null;
                  if (isTree && band.isContinuation) return null;
                  return (
                    <path
                      key={`${ribbon.id}-residual`}
                      d={ribbon.path}
                      fill="url(#differe-flow-hatch)"
                      pointerEvents="none"
                    />
                  );
                })}

                {layout.bands.map(band => (isTree && band.isContinuation ? null : (
                  <g key={band.id}>
                    <rect
                      x={band.x}
                      y={band.y}
                      width={band.width}
                      height={band.height}
                      rx={showCards ? 2 : 0}
                      className={[
                        'differe-stats-flow-band',
                        selectedId === band.id ? 'is-selected' : '',
                        band.node.isResidual ? 'is-residual' : '',
                        band.isContinuation ? 'is-continuation' : '',
                      ].filter(Boolean).join(' ')}
                      fill={colorScale.color(band.node)}
                      onClick={() => handleBandClick(band)}
                    >
                      <title>{bandTooltip(band, report.playerCount, metric, report.totalPlayersOnDeal)}</title>
                    </rect>
                    {showCards && (() => {
                      const inline = bandInlineLabel(band);
                      if (!inline) return null;
                      const cx = band.x + band.width / 2;
                      const cy = band.y + band.height / 2;
                      return (
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          transform={inline.vertical ? `rotate(-90 ${cx} ${cy})` : undefined}
                          className="differe-stats-flow-band-label"
                        >
                          {inline.text}
                        </text>
                      );
                    })()}
                  </g>
                )))}
              </g>

              <g transform={`translate(${AXIS_WIDTH}, ${HEADER_HEIGHT + PLOT_HEIGHT + 6})`}>
                <text x={-10} y={14} textAnchor="end" className="differe-stats-flow-axis-label">
                  η²
                </text>
                {layout.columns.map(({ column, x }) => {
                  const scaled = maxEta > 0 ? column.eta2Adj / maxEta : 0;
                  const barHeight = Math.round(scaled * 24);
                  return (
                    <g key={column.index}>
                      <rect
                        x={x}
                        y={26 - barHeight}
                        width={bandWidth}
                        height={Math.max(1, barHeight)}
                        className="differe-stats-flow-eta-bar"
                      >
                        <title>
                          {`${column.label} — variance du rang expliquée ${formatPct(column.eta2Adj)}`
                            + ` (η² ajusté, ${column.groupCount} choix distincts)\n`
                            + `Cumul depuis le départ ${formatPct(column.eta2CumAdj)}`}
                        </title>
                      </rect>
                      <text
                        x={x + bandWidth / 2}
                        y={38}
                        textAnchor="middle"
                        className="differe-stats-flow-eta-label"
                      >
                        {column.groupCount > 1 ? column.groupCount : ''}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <p className="admin-muted differe-stats-flow-legend">
            Hauteur d&apos;un courant = effectif.
            {' '}Couleur = {FLOW_METRIC_LABELS[metric].toLowerCase()} moyen des joueurs qui le
            composent. L&apos;ambre est la moyenne de tous les joueurs de la donne, le vert va
            jusqu&apos;à {colorScale.bestLabel} et le rouge jusqu&apos;à {colorScale.worstLabel} ;
            au-delà la teinte sature, pour qu&apos;une branche extrême ne repousse pas toutes les
            autres au milieu de l&apos;échelle. Un bon choix de carte fait verdir la sous-vague par
            rapport à celle dont elle est issue.
            {' '}La couleur d&apos;une sous-vague est ramenée vers celle de sa mère à proportion de
            ce que leur écart doit au hasard : tant que l&apos;écart se noie dans son erreur type,
            la sous-vague garde la teinte de sa mère. Sur chaque ruban, un dégradé horizontal
            part de la couleur de la mère et aboutit à celle de la fille : la teinte évolue là
            où la performance évolue, au lieu d&apos;apparaître d&apos;un coup à l&apos;arrivée.
            {' '}Courant hachuré « autres » : agrégat des branches sous le seuil d&apos;effectif ou
            au-delà des {report.params.maxChildren} conservées par nœud ; il garde la couleur de sa
            performance, se ramifie et reste cliquable comme les autres. La bande η² sous
            l&apos;axe donne la part de variance du rang expliquée par le choix de chaque colonne,
            corrigée du nombre de choix distincts.
            {ordering === 'wave'
              ? ' En ordre « vagues », l\'ordonnée reprend la même performance que la couleur :'
                + ' au départ d\'un intervalle les sœurs sont classées vert→rouge dans le créneau'
                + ' de leur mère (pas de croisement fraternel), et à l\'arrivée chacune se place'
                + ' librement sur l\'échelle — une sous-vague rouge issue d\'une mère verte peut'
                + ' donc plonger à travers les lignées voisines.'
              : ordering === 'flux'
                ? ' En ordre « flux », l\'ordonnée devient la position dans la donne au pli considéré,'
                  + ' en points de cartes cumulés lissés sur trois plis : les courants ondulent et se'
                  + ' croisent, au prix de la lisibilité des lignées.'
                : ' En ordre « ramification », l\'ordonnée ne porte aucun sens : elle sert seulement'
                  + ' à répartir l\'espace, chaque sous-vague restant dans le créneau de sa mère, ce'
                  + ' qui interdit tout croisement et laisse suivre une lignée de bout en bout.'}
          </p>

          {impacts.length > 0 && (
            <div className="differe-stats-flow-impacts">
              <h5>Décisions les plus lourdes de conséquence</h5>
              <ul>
                {impacts.map(impact => (
                  <li key={impact.band.id}>
                    <button
                      type="button"
                      className="admin-btn admin-btn-sm"
                      onClick={() => handleBandClick(impact.band)}
                    >
                      {impact.band.column.label} — {bandCardsLabel(impact.band)}
                    </button>
                    <span className={impact.delta < 0
                      ? 'differe-stats-flow-delta is-gain'
                      : 'differe-stats-flow-delta is-loss'}
                    >
                      {impact.delta < 0 ? '▲' : '▼'} {formatPct(Math.abs(impact.delta))} de rang
                    </span>
                    <span className="admin-muted">
                      {impact.band.node.count} joueur(s), rang moyen{' '}
                      {formatPct(impact.band.node.meanRankPct)} contre{' '}
                      {formatPct(impact.parentMeanRankPct)} pour la branche mère
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
